<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import {
  formatTemplateLabel,
  formatTemplateLanguage,
  templateStatusClasses,
  templateTypeKey,
} from './templateUtils';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
  teamsLoaded: {
    type: Boolean,
    default: false,
  },
  teamsUpdating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['preview', 'usageChange', 'teamsChange']);
const { t } = useI18n();
const teams = useMapGetter('teams/getTeams');
const isUsageMenuOpen = ref(false);
const assignedTeamIds = ref([]);

const showStatus = computed(
  () => props.template.status?.toLowerCase() !== 'approved'
);
const statusLabel = computed(() =>
  props.template.status?.toLowerCase() === 'unsubmitted'
    ? t('WHATSAPP_TEMPLATE_MGMT.STATUSES.UNSUBMITTED')
    : formatTemplateLabel(props.template.status)
);
const usageTypes = ['manual', 'campaign', 'both'];
const usageSettings = computed(() => props.template.chatwootSettings || []);
const usageType = computed(() => {
  const values = [
    ...new Set(usageSettings.value.map(setting => setting.usageType)),
  ];
  return values.length === 1 ? values[0] : 'mixed';
});
const usageMenuItems = computed(() =>
  usageTypes.map(type => ({
    label: t(`WHATSAPP_TEMPLATE_MGMT.USAGE_TYPES.${type.toUpperCase()}`),
    value: type,
    action: 'select-usage',
    isSelected: usageType.value === type,
  }))
);
const usageLabel = computed(() =>
  t(`WHATSAPP_TEMPLATE_MGMT.USAGE_TYPES.${usageType.value.toUpperCase()}`)
);
const canAssignTeams = computed(() =>
  ['manual', 'both', 'mixed'].includes(usageType.value)
);
const teamIdLists = computed(() =>
  usageSettings.value.map(setting => (setting.teamIds || []).map(Number))
);
const hasMixedTeamAssignments = computed(() => {
  const [firstTeamIds = []] = teamIdLists.value;
  const normalizedFirstTeamIds = [...new Set(firstTeamIds)].sort(
    (first, second) => first - second
  );

  return !teamIdLists.value.every(teamIds => {
    const normalizedTeamIds = [...new Set(teamIds)].sort(
      (first, second) => first - second
    );
    return (
      normalizedTeamIds.length === normalizedFirstTeamIds.length &&
      normalizedTeamIds.every(
        (teamId, index) => teamId === normalizedFirstTeamIds[index]
      )
    );
  });
});
const selectedTeamNames = computed(() =>
  assignedTeamIds.value.map(
    id => teams.value.find(team => Number(team.id) === id)?.name || ''
  )
);
const teamMenuItems = computed(() =>
  teams.value
    .filter(team => !assignedTeamIds.value.includes(Number(team.id)))
    .map(team => ({
      action: 'select',
      value: team.id,
      label: team.name,
    }))
);
const syncAssignedTeams = () => {
  assignedTeamIds.value = [...new Set(teamIdLists.value.flat())];
};
const addTeam = ({ value }) => {
  const teamId = Number(value);
  if (assignedTeamIds.value.includes(teamId)) return;

  assignedTeamIds.value = [...assignedTeamIds.value, teamId];
  emit('teamsChange', {
    template: props.template,
    teamIds: assignedTeamIds.value,
  });
};
const removeTeam = index => {
  assignedTeamIds.value = assignedTeamIds.value.filter((_, i) => i !== index);
  emit('teamsChange', {
    template: props.template,
    teamIds: assignedTeamIds.value,
  });
};
const selectUsageType = ({ value }) => {
  isUsageMenuOpen.value = false;
  emit('usageChange', { template: props.template, usageType: value });
};

watch(() => props.template.chatwootSettings, syncAssignedTeams, {
  deep: true,
  immediate: true,
});
</script>

<template>
  <div
    class="flex items-center justify-between gap-4 py-4 cursor-pointer group"
    role="button"
    tabindex="0"
    @click="emit('preview')"
    @keydown.enter="emit('preview')"
    @keydown.space.prevent="emit('preview')"
  >
    <div class="flex items-center min-w-0 gap-3">
      <span
        class="grid border rounded-xl shadow-sm size-10 shrink-0 place-items-center bg-n-alpha-3 border-n-strong ring ring-n-solid-1"
      >
        <ChannelIcon
          :inbox="template.inboxes[0]"
          class="size-5 text-n-slate-11"
        />
      </span>
      <div class="flex flex-col min-w-0 gap-1">
        <div class="flex items-center min-w-0 gap-2">
          <span class="truncate text-heading-3 text-n-slate-12">
            {{ template.name }}
          </span>
          <span
            v-if="showStatus"
            class="inline-flex shrink-0 px-2 py-0.5 text-xs font-medium rounded-md"
            :class="templateStatusClasses(template.status)"
          >
            {{ statusLabel }}
          </span>
        </div>
        <div
          class="flex flex-wrap items-center gap-2 text-body-main text-n-slate-11"
        >
          <span>
            {{
              $t(`WHATSAPP_TEMPLATE_MGMT.TYPES.${templateTypeKey(template)}`)
            }}
          </span>
          <div class="w-px h-3 rounded-lg bg-n-strong" />
          <span>{{ formatTemplateLanguage(template.language) }}</span>
          <div class="w-px h-3 rounded-lg bg-n-strong" />
          <span class="truncate">{{ template.inboxNames }}</span>
          <div
            v-if="usageSettings.length"
            v-on-click-outside="() => (isUsageMenuOpen = false)"
            class="relative"
            @click.stop
          >
            <Button
              color="slate"
              size="xs"
              :label="usageLabel"
              trailing-icon
              icon="i-lucide-tag"
              @click="isUsageMenuOpen = !isUsageMenuOpen"
            />
            <DropdownMenu
              v-if="isUsageMenuOpen"
              :menu-items="usageMenuItems"
              class="mt-2 min-w-44 top-full ltr:right-0 rtl:left-0"
              @action="selectUsageType"
            />
          </div>
          <div
            v-if="usageSettings.length && canAssignTeams && teamsLoaded"
            class="min-w-52 max-w-80"
            :class="{ 'opacity-60': teamsUpdating }"
            :aria-busy="teamsUpdating"
            @click.stop
            @keydown.stop
          >
            <span
              v-if="hasMixedTeamAssignments"
              class="block mb-1 text-xs text-n-slate-10"
            >
              {{ $t('WHATSAPP_TEMPLATE_MGMT.TEAMS_MIXED') }}
            </span>
            <TagInput
              :model-value="selectedTeamNames"
              :placeholder="$t('WHATSAPP_TEMPLATE_MGMT.TEAMS_PLACEHOLDER')"
              :menu-items="teamMenuItems"
              :disabled="teamsUpdating"
              show-dropdown
              skip-label-dedup
              :auto-open-dropdown="false"
              @add="addTeam"
              @remove="removeTeam"
            />
          </div>
        </div>
      </div>
    </div>
    <Button
      v-tooltip.top="$t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.TITLE')"
      icon="i-lucide-eye"
      color="slate"
      size="sm"
      class="shrink-0"
      :aria-label="
        $t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.OPEN', { name: template.name })
      "
      @click.stop="emit('preview')"
    />
  </div>
</template>
